import { Card } from '../ui/Card.jsx';
import { Button } from '../ui/Button.jsx';
import { useState, useEffect, useRef } from 'react';
import { calculateTrade, canExecuteTrade } from '../../systems/tradeSystem.js';
import { useAudio } from '../../hooks/useAudio.js';

const RESOURCE_LABELS = {
  food: '🍖 Food',
  wood: '🪵 Wood',
  stone: '🪨 Stone',
  metal: '⚙️ Metal'
};

export function TradeDialog({ diceTotal, currentResources, onConfirmTrade, onCancel }) {
  const audio = useAudio();
  const dialogRef = useRef(null);

  const [fromResource, setFromResource] = useState('food');
  const [toResource, setToResource] = useState('wood');

  const tradeableResources = ['food', 'wood', 'stone', 'metal'].filter(
    r => (currentResources[r] || 0) > 0
  );

  const targetResources = ['food', 'wood', 'stone', 'metal'].filter(
    r => r !== fromResource
  );

  // Calculate trade preview
  const tradeResult = calculateTrade(fromResource, toResource, diceTotal);
  const canTrade = canExecuteTrade(tradeResult, currentResources);

  // Auto-adjust if from/to are the same
  useEffect(() => {
    if (fromResource === toResource) {
      const alternatives = targetResources.filter(r => r !== fromResource);
      if (alternatives.length > 0) {
        setToResource(alternatives[0]);
      }
    }
  }, [fromResource, toResource, targetResources]);

  // Open dialog on mount
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    audio.playSound('ui_open');
    dialog.showModal();

    const handleCancel = (e) => {
      e.preventDefault();
      audio.playSound('ui_close');
      onCancel();
    };

    dialog.addEventListener('cancel', handleCancel);
    return () => dialog.removeEventListener('cancel', handleCancel);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirm = () => {
    if (canTrade) {
      audio.playSound('ui_confirm');
      audio.playSound('ui_close');
      onConfirmTrade(tradeResult);
    } else {
      audio.playSound('ui_error');
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className="backdrop:bg-black backdrop:bg-opacity-50 rounded-lg p-0 max-w-md"
    >
      <Card title="Trade Resources" icon="💰">
        <div className="mb-4 p-3 bg-blue-100 rounded-lg text-center">
          <div className="font-bold text-blue-900">Trade Volume: {diceTotal}</div>
          <div className="text-sm text-blue-700">Based on your workers&apos; dice rolls</div>
        </div>

        {/* From Resource Selector */}
        <div className="mb-4">
          <label className="block font-bold text-amber-900 mb-2">Trade Away:</label>
          <select
            value={fromResource}
            onChange={(e) => setFromResource(e.target.value)}
            className="w-full p-2 border-2 border-amber-600 rounded bg-white"
          >
            {tradeableResources.map(r => (
              <option key={r} value={r}>
                {RESOURCE_LABELS[r]} ({currentResources[r] || 0} available)
              </option>
            ))}
          </select>
        </div>

        {/* To Resource Selector */}
        <div className="mb-4">
          <label className="block font-bold text-amber-900 mb-2">Receive:</label>
          <select
            value={toResource}
            onChange={(e) => setToResource(e.target.value)}
            className="w-full p-2 border-2 border-amber-600 rounded bg-white"
          >
            {targetResources.map(r => (
              <option key={r} value={r}>{RESOURCE_LABELS[r]}</option>
            ))}
          </select>
        </div>

        {/* Trade Preview */}
        {tradeResult.valid && (
          <div className={`mb-4 p-3 rounded-lg ${canTrade ? 'bg-green-100 border-2 border-green-600' : 'bg-red-100 border-2 border-red-600'}`}>
            <div className="text-center">
              <div className="font-bold text-lg mb-2">
                {RESOURCE_LABELS[fromResource]}: -{tradeResult.cost}
                {' → '}
                {RESOURCE_LABELS[toResource]}: +{tradeResult.gained}
              </div>
              <div className="text-sm">
                Exchange rate: 1 {fromResource} = {tradeResult.rate} {toResource}
              </div>
              {!canTrade && (
                <div className="text-red-700 font-bold mt-2">
                  Not enough {fromResource}!
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            onClick={handleConfirm}
            disabled={!canTrade}
            className="flex-1"
          >
            Confirm Trade
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              audio.playSound('ui_close');
              onCancel();
            }}
            className="flex-1"
          >
            Cancel
          </Button>
        </div>
      </Card>
    </dialog>
  );
}
